"use client";

import { useEffect, useRef, useState } from "react";

const levels = [
  {
    level: "Niveau 0",
    name: "Artisanal",
    biens: "< 5 logements",
    standardisation: "❌ Aucune",
    centralisation: "❌ Aucune",
    automatisation: "❌ Aucune",
    pilotage: "❌ Aucun",
    risk: "Stable à court terme",
    color: "#9CA3AF",
    bg: "rgba(156,163,175,0.06)",
  },
  {
    level: "Niveau 1",
    name: "En transition",
    biens: "5–10 logements",
    standardisation: "⚠️ Partielle",
    centralisation: "⚠️ Partielle",
    automatisation: "❌ Aucune",
    pilotage: "❌ Aucun",
    risk: "Tensions opérationnelles",
    color: "#C9A84C",
    bg: "rgba(201,168,76,0.06)",
  },
  {
    level: "Niveau 2",
    name: "Structuré",
    biens: "10–20 logements",
    standardisation: "✅ Complète",
    centralisation: "✅ Complète",
    automatisation: "⚠️ Partielle",
    pilotage: "⚠️ Partiel",
    risk: "Croissance maîtrisée",
    color: "#6B7A5E",
    bg: "rgba(107,122,94,0.06)",
    highlighted: true,
  },
  {
    level: "Niveau 3",
    name: "Industrialisé",
    biens: "20+ logements",
    standardisation: "✅ Complète",
    centralisation: "✅ Complète",
    automatisation: "✅ Complète",
    pilotage: "✅ Complet",
    risk: "Scalabilité totale",
    color: "#4A6741",
    bg: "rgba(74,103,65,0.08)",
  },
];

export default function MatriceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="matrice"
      ref={sectionRef}
      className="py-28 md:py-40 px-6 md:px-16"
      style={{ background: "var(--ivory)" }}
    >
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div
          className={`flex items-center gap-6 mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="font-serif italic text-sm" style={{ color: "var(--sage)" }}>
            Page 08
          </span>
          <div className="gold-line" />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--warm-gray)" }}>
            Matrice de maturité
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-16">
          <div className="lg:col-span-6">
            <h2
              className={`font-serif italic leading-tight mb-6 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "var(--charcoal)" }}
            >
              À quel niveau êtes-vous{" "}
              <span className="not-italic" style={{ color: "var(--sage)" }}>aujourd'hui ?</span>
            </h2>
            <p
              className={`text-base leading-relaxed transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ color: "var(--warm-gray)" }}
            >
              Cette matrice synthétise les 4 piliers selon le stade de maturité opérationnelle de votre conciergerie. Identifiez votre niveau actuel pour cibler vos prochaines actions prioritaires.
            </p>
          </div>
        </div>

        {/* Matrix table */}
        <div
          className={`overflow-x-auto transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <table className="w-full min-w-[800px]" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th
                  className="text-left py-4 px-6 text-xs font-bold uppercase tracking-widest"
                  style={{ color: "var(--warm-gray)", borderBottom: "2px solid var(--border)" }}
                >
                  Niveau
                </th>
                {["Standardisation", "Centralisation", "Automatisation", "Pilotage", "Résultat"]?.map((h) => (
                  <th
                    key={h}
                    className="text-left py-4 px-6 text-xs font-bold uppercase tracking-widest"
                    style={{ color: "var(--warm-gray)", borderBottom: "2px solid var(--border)" }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {levels?.map((row, i) => (
                <tr
                  key={i}
                  className="matrix-row"
                  style={{
                    background: row?.highlighted ? row?.bg : undefined,
                    borderLeft: row?.highlighted ? `3px solid ${row?.color}` : undefined,
                  }}
                >
                  <td className="py-5 px-6" style={{ borderBottom: "1px solid var(--border)" }}>
                    <div className="flex flex-col gap-1">
                      <span
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: row?.color }}
                      >
                        {row?.level}
                      </span>
                      <span className="font-serif italic text-lg" style={{ color: "var(--charcoal)" }}>
                        {row?.name}
                      </span>
                      <span className="text-xs" style={{ color: "var(--warm-gray)" }}>
                        {row?.biens}
                      </span>
                    </div>
                  </td>
                  <td className="py-5 px-6 text-sm" style={{ borderBottom: "1px solid var(--border)", color: "var(--charcoal)" }}>
                    {row?.standardisation}
                  </td>
                  <td className="py-5 px-6 text-sm" style={{ borderBottom: "1px solid var(--border)", color: "var(--charcoal)" }}>
                    {row?.centralisation}
                  </td>
                  <td className="py-5 px-6 text-sm" style={{ borderBottom: "1px solid var(--border)", color: "var(--charcoal)" }}>
                    {row?.automatisation}
                  </td>
                  <td className="py-5 px-6 text-sm" style={{ borderBottom: "1px solid var(--border)", color: "var(--charcoal)" }}>
                    {row?.pilotage}
                  </td>
                  <td className="py-5 px-6" style={{ borderBottom: "1px solid var(--border)" }}>
                    <span
                      className="text-xs font-bold px-3 py-1.5 rounded-full"
                      style={{ background: row?.bg, color: row?.color, border: `1px solid ${row?.color}40` }}
                    >
                      {row?.risk}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Tip for Level 2 */}
        <div
          className={`mt-16 rounded-2xl p-10 transition-all duration-700 delay-400 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{
            background: "rgba(107,122,94,0.06)",
            border: "1px solid rgba(107,122,94,0.2)",
          }}
        >
          <div className="flex items-start gap-6">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "var(--sage)", color: "white" }}
            >
              <span className="text-lg">💡</span>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{ background: "var(--sage)", color: "white" }}
                >
                  Tip stratégique · Niveau 2
                </span>
              </div>
              <h3 className="font-serif italic text-xl mb-3" style={{ color: "var(--charcoal)" }}>
                Vous êtes au Niveau 2 ? Voici votre prochaine priorité.
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--warm-gray)" }}>
                Le passage du Niveau 2 au Niveau 3 repose sur deux actions précises :{" "}
                <strong style={{ color: "var(--charcoal)" }}>automatiser vos séquences voyageurs</strong>{" "}
                (J-3, J+1, J+2) et{" "}
                <strong style={{ color: "var(--charcoal)" }}>mettre en place un tableau de bord KPIs mensuel</strong>.
                Ces deux actions seules peuvent faire passer votre taux d'avis 5★ de 75% à 90%+ en moins de 90 jours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}